import React from "react";
import { Modal } from "antd";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import moment from "moment";
import ConfigurationPanel from "../configuration/ConfigurationPanel";
import TestRunRequestComparison from "./TestRunRequestComparison";
import RequestRepresentationModal from "../Request/RequestRepresentationModal";
import TestRunRequestsTable from "./TestRunRequestsTable";
import TestRunEvaluations from "./Steps/TestRunEvaluations";
import TestRunStatusBadge from "./TestRunStatusBadge";
import TestRunResultsSummary from "./TestRunResultsSummary";
import TestRunScoreFilter from "./TestRunScoreFilter";
import TestRunBreadcrumb from "./TestRunBreadcrumb";
import { useTestRunDetail } from "./useTestRunDetail";
import { PlayIcon } from "lucide-react";

const TestRunDetail: React.FC = () => {
  const {
    intentGroupId,
    testSetId,
    testRunId,
    apiKey,
    intentGroup,
    testSet,
    testRun,
    isLoading,
    requests,
    isRequestsLoading,
    currentPage,
    setCurrentPage,
    totalRequests,
    testRunResults,
    isResultsLoading,
    selectedScores,
    selectedCriteria,
    progress,
    isConfigModalVisible,
    setIsConfigModalVisible,
    isComparisonModalVisible,
    setIsComparisonModalVisible,
    selectedComparisonRequestId,
    setSelectedComparisonRequestId,
    selectedRequestId,
    setSelectedRequestId,
    isRepresentationModalVisible,
    setIsRepresentationModalVisible,
    isEvalModalVisible,
    setIsEvalModalVisible,
    evalCriteriaIds,
    setEvalCriteriaIds,
    isEvalRunning,
    handleRowClick,
    handleScoreClick,
    handleCompareClick,
    handleScoreFilterClear,
    handleCriteriaSelect,
    handleRunEvaluations,
  } = useTestRunDetail();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        <Card className="border-border">
          <CardContent className="p-8">
            <TestRunBreadcrumb
              intentGroup={intentGroup}
              testSet={testSet}
              testRun={testRun}
              testSetId={testSetId}
            />

            {isLoading ? (
              <div className="space-y-6">
                <Skeleton className="h-10 w-64" />
                <div className="grid md:grid-cols-4 gap-6">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="space-y-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-5 w-32" />
                    </div>
                  ))}
                </div>
              </div>
            ) : testRun ? (
              <>
                <div className="flex items-center gap-4 mb-6">
                  <h1 className="text-3xl font-gc-demi-bold text-foreground tracking-tight">
                    {testRun.description || `Test Run ${testRun.id}`}
                  </h1>
                  <TestRunStatusBadge testRun={testRun} progress={progress} />
                </div>
                <div className="grid md:grid-cols-4 gap-x-8 gap-y-4 text-sm">
                  <div>
                    <span className="text-muted-foreground font-gc-reg">
                      Test Set:{" "}
                    </span>
                    <span className="text-foreground font-gc-reg">
                      {testSet?.name || "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground font-gc-reg">
                      Model:{" "}
                    </span>
                    <Button
                      variant="ghost"
                      className="p-0 h-auto text-sm font-gc-reg text-foreground hover:text-maitai-green transition-colors underline"
                      onClick={() => setIsConfigModalVisible(true)}
                    >
                      {testRun.config?.model || "No model specified"}
                    </Button>
                  </div>
                  <div>
                    <span className="text-muted-foreground font-gc-reg">
                      Created:{" "}
                    </span>
                    <span className="text-foreground font-gc-reg">
                      {moment(testRun.date_created).format(
                        "MMM D, YYYY HH:mm"
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground font-gc-reg">
                      Completed:{" "}
                    </span>
                    <span className="text-foreground font-gc-reg">
                      {testRun.date_completed
                        ? moment(testRun.date_completed).format(
                            "MMM D, YYYY HH:mm"
                          )
                        : "—"}
                    </span>
                  </div>
                </div>
                {!isResultsLoading && (
                  <TestRunResultsSummary
                    testRun={testRun}
                    testRunResults={testRunResults}
                    onRunEvaluations={() => setIsEvalModalVisible(true)}
                  />
                )}
              </>
            ) : null}
          </CardContent>
        </Card>

        <Modal
          title="Run Evaluations"
          open={isEvalModalVisible}
          onCancel={() => setIsEvalModalVisible(false)}
          width={700}
          footer={[
            <Button
              key="cancel"
              variant="ghost"
              onClick={() => setIsEvalModalVisible(false)}
            >
              Cancel
            </Button>,
            <Button
              key="run"
              disabled={evalCriteriaIds.length === 0 || isEvalRunning}
              onClick={handleRunEvaluations}
              className="gap-2"
            >
              <PlayIcon className="w-4 h-4" />
              {isEvalRunning ? "Starting..." : "Run Selected Criteria"}
            </Button>,
          ]}
        >
          <div className="max-h-[60vh] overflow-y-auto">
            <TestRunEvaluations
              selectedCriteriaIds={evalCriteriaIds}
              onCriteriaChange={setEvalCriteriaIds}
            />
          </div>
        </Modal>

        <Card className="border-border">
          <CardContent className="p-8">
            <h2 className="text-xl font-gc-med text-foreground mb-4">
              Requests
            </h2>
            <TestRunScoreFilter
              testRunResults={testRunResults}
              selectedCriteria={selectedCriteria}
              selectedScores={selectedScores}
              onCriteriaSelect={handleCriteriaSelect}
              onScoreClick={handleScoreClick}
              onScoreFilterClear={handleScoreFilterClear}
            />
            <TestRunRequestsTable
              requests={requests}
              loading={isRequestsLoading}
              apiKey={apiKey}
              currentPage={currentPage}
              totalPages={Math.ceil(totalRequests / 10)}
              totalItems={totalRequests}
              itemsPerPage={10}
              onPageChange={setCurrentPage}
              onRowClick={handleRowClick}
              onCompareClick={handleCompareClick}
              testSetId={testSetId}
              systemPrompt={testRun?.config?.system_prompt}
              selectedScores={selectedScores}
              onScoreFilterClear={handleScoreFilterClear}
            />
          </CardContent>
        </Card>

        <Modal
          title="Test Run Configuration"
          open={isConfigModalVisible}
          onCancel={() => setIsConfigModalVisible(false)}
          footer={null}
          width={800}
        >
          <ConfigurationPanel
            intentGroupId={intentGroupId ? Number(intentGroupId) : null}
            config={testRun?.config}
            updateConfig={() => {}}
            resetConfig={() => {}}
            isModalOpen={true}
            readOnly={true}
            allowSecondaryModel={false}
            showSystemPrompt={true}
          />
        </Modal>

        {selectedComparisonRequestId && (
          <TestRunRequestComparison
            testRunId={Number(testRunId)}
            testRunRequestId={selectedComparisonRequestId}
            isVisible={isComparisonModalVisible}
            onClose={() => {
              setIsComparisonModalVisible(false);
              setSelectedComparisonRequestId(null);
            }}
            apiKey={apiKey}
            testSetId={testSetId}
            systemPrompt={testRun?.config?.system_prompt}
            intentGroupId={intentGroupId}
          />
        )}

        {selectedRequestId && (
          <RequestRepresentationModal
            requestId={selectedRequestId}
            isVisible={isRepresentationModalVisible}
            onClose={() => {
              setIsRepresentationModalVisible(false);
              setSelectedRequestId(null);
            }}
            apiKey={apiKey}
          />
        )}
      </div>
    </div>
  );
};

export default TestRunDetail;
